import type { Position, BonusType } from '../types';
import {
  BOARD_SIZE,
  BONUS_SQUARE_TARGET_COUNT,
  BONUS_SQUARE_MIN_DISTANCE,
  BONUS_DISTRIBUTION,
} from '../constants/gameConstants';

export interface BonusSquare {
  position: Position;
  type: BonusType;
}

/**
 * Generate bonus squares using Poisson disk sampling for even distribution
 * 
 * @param seed - Optional random seed for deterministic generation
 * @returns Array of bonus squares
 */
export function generateBonusSquares(seed?: string | number): BonusSquare[] {
  const bonusSquares: BonusSquare[] = [];
  const occupiedPositions = new Set<string>();
  
  // Convert string seed to number if needed
  const numericSeed = seed !== undefined 
    ? typeof seed === 'string' 
      ? Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : seed
    : undefined;
  
  // Use seeded random if provided
  const random = numericSeed !== undefined ? createSeededRandom(numericSeed) : Math.random;
  
  // Calculate how many of each type to generate
  const bonusCounts: Record<BonusType, number> = {
    DL: Math.round(BONUS_SQUARE_TARGET_COUNT * BONUS_DISTRIBUTION.DL),
    DW: Math.round(BONUS_SQUARE_TARGET_COUNT * BONUS_DISTRIBUTION.DW),
    TL: Math.round(BONUS_SQUARE_TARGET_COUNT * BONUS_DISTRIBUTION.TL),
    TW: Math.round(BONUS_SQUARE_TARGET_COUNT * BONUS_DISTRIBUTION.TW),
  };
  
  // Generate each type of bonus square
  const bonusTypes: BonusType[] = ['DL', 'DW', 'TL', 'TW'];
  
  for (const bonusType of bonusTypes) {
    let attempts = 0;
    const maxAttempts = bonusCounts[bonusType] * 100; // Avoid infinite loops
    
    while (bonusSquares.filter(b => b.type === bonusType).length < bonusCounts[bonusType] && attempts < maxAttempts) {
      attempts++;
      
      // Generate random position
      const row = Math.floor(random() * BOARD_SIZE);
      const col = Math.floor(random() * BOARD_SIZE);
      const position = { row, col };
      const posKey = `${row},${col}`;
      
      // Skip if already occupied
      if (occupiedPositions.has(posKey)) continue;
      
      // Check minimum distance from existing bonus squares
      if (isValidBonusPosition(position, bonusSquares)) {
        bonusSquares.push({ position, type: bonusType });
        occupiedPositions.add(posKey);
      }
    }
  }
  
  return bonusSquares;
}

/**
 * Check if a position is valid (maintains minimum distance from other bonuses)
 */
function isValidBonusPosition(position: Position, existingBonuses: BonusSquare[]): boolean {
  for (const bonus of existingBonuses) {
    // Use Manhattan distance (sum of absolute differences)
    const distance = 
      Math.abs(position.row - bonus.position.row) +
      Math.abs(position.col - bonus.position.col);
    
    if (distance < BONUS_SQUARE_MIN_DISTANCE) {
      return false;
    }
  }
  
  return true;
}

/**
 * Create a seeded random number generator
 * Simple Linear Congruential Generator for deterministic randomness
 */
function createSeededRandom(seed: number): () => number {
  let state = seed;
  
  return function() {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

/**
 * Get bonus square at a specific position
 */
export function getBonusAtPosition(bonusSquares: BonusSquare[], position: Position): BonusType | null {
  const bonus = bonusSquares.find(
    b => b.position.row === position.row && b.position.col === position.col
  );
  return bonus?.type ?? null;
}
